// Dynamic loading from CDN using fetch
let parsePhoneNumberWithError: unknown,
  isValidPhoneNumber: unknown,
  metadata: unknown,
  countries: string[]
const countryNames = new Intl.DisplayNames(["en"], { type: "region" })

// Load flag-icons CSS
const loadFlagIcons = (): Promise<void> => {
  return new Promise<void>((resolve) => {
    const existing = document.querySelector('link[href*="flag-icons"]')
    if (existing) {
      resolve()
      return
    }

    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href =
      "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/css/flag-icons.min.css"
    link.onload = () => resolve()
    link.onerror = () => resolve() // Resolve anyway to not block
    document.head.appendChild(link)
  })
}

// Load dependencies from CDN
const loadDependencies = async () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, _libphonenumberResponse, metadataResponse] = await Promise.all([
    loadFlagIcons(),
    new Promise<void>((resolve, reject) => {
      const script = document.createElement("script")
      script.src =
        "https://cdn.jsdelivr.net/npm/libphonenumber-js@1.12.24/bundle/libphonenumber-max.js"

      script.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).libphonenumber) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          parsePhoneNumberWithError = (window as any).libphonenumber
            .parsePhoneNumberWithError
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          isValidPhoneNumber = (window as any).libphonenumber.isValidPhoneNumber
          resolve()
        } else {
          reject(new Error("libphonenumber not loaded"))
        }
      }
      script.onerror = () => reject(new Error("Failed to load libphonenumber"))
      document.head.appendChild(script)
    }),
    fetch(
      "https://cdn.jsdelivr.net/npm/libphonenumber-js@1.12.24/metadata.full.json"
    ).then((r) => r.json()),
  ])

  metadata = metadataResponse
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countries = Object.keys((metadata as any).countries)
}

class IntlPhoneInput extends HTMLElement {
  isValidPhoneNumber: boolean
  phoneNumber: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parsedNumber: any
  country: string | null
  flagIcon: HTMLSpanElement | null
  countryCode: HTMLSpanElement | null
  flagButton: HTMLButtonElement | null
  _popover: HTMLDivElement | null
  _searchInput: HTMLInputElement | null
  countryList: HTMLDivElement | null
  countryOptions: HTMLButtonElement[]
  input: HTMLInputElement | null
  hiddenInput: HTMLInputElement | null

  constructor() {
    super()
    this.isValidPhoneNumber = false
    this.phoneNumber = null
    this.parsedNumber = null
    this.country = null
    this.flagIcon = null
    this.countryCode = null
    this.flagButton = null
    this._popover = null
    this._searchInput = null
    this.countryList = null
    this.countryOptions = []
    this.input = null
    this.hiddenInput = null
  }

  changeCountry(country: string): void {
    this.country = country
    this.flagIcon!.className = `fi fi-${country.toLowerCase()}`
    this.countryCode!.textContent = country

    // Update selected state in popover
    this.countryOptions.forEach((option) => {
      if (option.dataset.country === country) {
        option.classList.add("selected")
      } else {
        option.classList.remove("selected")
      }
    })

    // Revalidate phone number with new country
    if (this.input && this.phoneNumber) {
      this.validatePhoneNumber()
    }
  }

  validatePhoneNumber(): void {
    this.phoneNumber = this.input?.value || ""
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.isValidPhoneNumber = (isValidPhoneNumber as any)(
      this.phoneNumber,
      this.country || undefined
    )

    if (this.isValidPhoneNumber) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parsedNumber = (parsePhoneNumberWithError as any)(
          this.phoneNumber,
          this.country || "AU"
        )
        this.parsedNumber = parsedNumber

        // Update hidden input with parsed phone number in international format
        if (this.hiddenInput && this.parsedNumber) {
          this.hiddenInput.value = this.parsedNumber
            .formatInternational()
            .replace(/ /g, "")
          this.hiddenInput.dispatchEvent(
            new Event("parsed_phone_change", { bubbles: true })
          )
        }
      } catch (error: unknown) {
        console.log("Parse error:", (error as Error).message)
        // Clear hidden input if parsing fails
        if (this.hiddenInput) {
          this.hiddenInput.value = ""
          this.hiddenInput.dispatchEvent(
            new Event("parsed_phone_change", { bubbles: true })
          )
        }
      }
    } else {
      // Clear hidden input if number is invalid
      if (this.hiddenInput) {
        this.hiddenInput.value = ""
        this.hiddenInput.dispatchEvent(
          new Event("parsed_phone_change", { bubbles: true })
        )
      }
    }

    this.input?.setAttribute("aria-invalid", `${!this.isValidPhoneNumber}`)
  }

  togglePopover(): boolean {
    const isOpen = this._popover!.classList.toggle("open")
    this.flagButton!.classList.toggle("open")

    if (isOpen) {
      this._searchInput!.value = ""
      this.filterCountries("")
      this._searchInput!.focus()
      // Scroll selected country into view
      const selected = this._popover!.querySelector(".country-option.selected")
      if (selected) {
        selected.scrollIntoView({ block: "nearest" })
      }
    }

    return isOpen
  }

  closePopover(): void {
    this._popover!.classList.remove("open")
    this.flagButton!.classList.remove("open")
  }

  filterCountries(searchTerm: string): void {
    const term = searchTerm.toLowerCase()
    let hasResults = false

    this.countryOptions.forEach((option) => {
      const countryName = (option.dataset.countryName || "").toLowerCase()
      const countryCode = (option.dataset.country || "").toLowerCase()
      const matches = countryName.includes(term) || countryCode.includes(term)

      ;(option as HTMLElement).style.display = matches ? "flex" : "none"
      if (matches) hasResults = true
    })

    // Show/hide no results message
    const noResults = this._popover!.querySelector(".no-results")
    if (noResults) {
      ;(noResults as HTMLElement).style.display = hasResults ? "none" : "block"
    } else if (!hasResults) {
      const msg = document.createElement("div")
      msg.className = "no-results"
      msg.textContent = "No countries found"
      this.countryList!.appendChild(msg)
    }
  }

  async connectedCallback(): Promise<void> {
    // Load dependencies if not already loaded
    if (!countries) {
      await loadDependencies()
    }

    this.country = this.getAttribute("default-country") ?? "AU"
    this.input = this.querySelector('input[type="tel"]')
    this.hiddenInput = this.querySelector('input[type="hidden"]')
    this.isValidPhoneNumber = false

    // Create flag button
    this.flagButton = document.createElement("button")
    this.flagButton.className = "flag-button"
    this.flagButton.type = "button"

    this.flagIcon = document.createElement("span")
    this.flagIcon.className = "fi"

    this.countryCode = document.createElement("span")
    this.countryCode.textContent = this.country

    const arrow = document.createElement("span")
    arrow.className = "arrow"
    arrow.textContent = "▼"

    this.flagButton.appendChild(this.flagIcon)
    this.flagButton.appendChild(this.countryCode)
    this.flagButton.appendChild(arrow)

    // Create popover
    this._popover = document.createElement("div")
    this._popover.className = "country-popover"

    // Create search input
    this._searchInput = document.createElement("input")
    this._searchInput.type = "text"
    this._searchInput.className = "country-search"
    this._searchInput.placeholder = "Search countries..."
    this._searchInput.setAttribute("aria-label", "Search countries")

    // Create country list
    this.countryList = document.createElement("div")
    this.countryList.className = "country-list"
    this.countryList.setAttribute("role", "listbox")

    // Populate country options
    this.countryOptions = []
    countries.forEach((country) => {
      const option = document.createElement("button")
      option.type = "button"
      option.className = "country-option"
      option.dataset.country = country
      option.dataset.countryName = countryNames.of(country) || country
      option.setAttribute("role", "option")

      const flag = document.createElement("span")
      flag.className = `fi fi-${country.toLowerCase()}`

      const name = document.createElement("span")
      name.className = "country-name"
      name.textContent = countryNames.of(country) || country

      const code = document.createElement("span")
      code.className = "country-code"
      code.textContent = country

      option.appendChild(flag)
      option.appendChild(name)
      option.appendChild(code)

      option.addEventListener("click", () => {
        this.changeCountry(country)
        this.closePopover()
        this.input?.focus()
      })

      this.countryList!.appendChild(option)
      this.countryOptions.push(option)
    })

    this._popover.appendChild(this._searchInput)
    this._popover.appendChild(this.countryList)

    // Insert elements
    if (this.input) {
      this.insertBefore(this.flagButton, this.input)
    } else {
      this.appendChild(this.flagButton)
    }
    this.appendChild(this._popover)

    // Set initial country
    this.changeCountry(this.country)

    // Event listeners
    this.flagButton.addEventListener("click", (e) => {
      e.stopPropagation()
      this.togglePopover()
    })

    this._searchInput.addEventListener("input", (e) => {
      this.filterCountries((e.target as HTMLInputElement).value)
    })

    this._searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closePopover()
        this.flagButton?.focus()
      } else if (e.key === "ArrowDown") {
        e.preventDefault()
        const firstVisible = this.countryList?.querySelector(
          '.country-option:not([style*="display: none"])'
        )
        if (firstVisible) (firstVisible as HTMLElement).focus()
      }
    })

    // Keyboard navigation in country list
    this.countryList.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closePopover()
        this.flagButton?.focus()
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault()
        const options = Array.from(
          this.countryList!.querySelectorAll(
            '.country-option:not([style*="display: none"])'
          )
        )
        const currentIndex = options.indexOf(document.activeElement as Element)

        if (e.key === "ArrowDown" && currentIndex < options.length - 1) {
          ;(options[currentIndex + 1] as HTMLElement).focus()
        } else if (e.key === "ArrowUp") {
          if (currentIndex > 0) {
            ;(options[currentIndex - 1] as HTMLElement).focus()
          } else {
            this._searchInput?.focus()
          }
        }
      } else if (
        e.key === "Enter" &&
        document.activeElement?.classList.contains("country-option")
      ) {
        ;(document.activeElement as HTMLElement).click()
      }
    })

    // Close popover when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target as Node)) {
        this.closePopover()
      }
    })

    this.input?.addEventListener("input", () => {
      this.validatePhoneNumber()
    })
  }
}

if (!customElements.get("intl-phone-input")) {
  customElements.define("intl-phone-input", IntlPhoneInput)
}
